import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/lib/utils/env';
import { ApiError, type ApiEnvelope } from './types';

/**
 * Central axios instance.
 *
 * Responsibilities:
 *   - Prefix every request with the configured API base URL
 *   - Attach Authorization: Bearer <token> when present
 *   - Unwrap `{ success, data }` envelopes into bare data
 *   - Throw `ApiError` for `{ success: false }` responses (and non-2xx)
 *   - On 401: refresh token once (single-flight), retry original request
 */

const TOKEN_STORAGE_KEY = 'uo.auth.token';
const REFRESH_STORAGE_KEY = 'uo.auth.refresh';

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_STORAGE_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_STORAGE_KEY),
  set: (token: string, refresh?: string) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    if (refresh) localStorage.setItem(REFRESH_STORAGE_KEY, refresh);
  },
  clear: () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_STORAGE_KEY);
  },
};

const baseURL = env.apiBaseUrl || '/api/v1';

export const api: AxiosInstance = axios.create({
  baseURL,
  timeout: 20_000,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

/* ── Request: attach auth token ───────────────────────────────────────── */
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.get();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/* ── Single-flight refresh-on-401 ─────────────────────────────────────── */
let refreshInFlight: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  const refresh = tokenStore.getRefresh();
  if (!refresh) return null;
  try {
    const { data } = await axios.post<ApiEnvelope<{ access_token: string; refresh_token: string }>>(
      `${baseURL}/marketplace/auth/refresh`,
      { refresh_token: refresh },
      { headers: { 'Content-Type': 'application/json' } },
    );
    if (data.success) {
      tokenStore.set(data.data.access_token, data.data.refresh_token);
      return data.data.access_token;
    }
    return null;
  } catch {
    return null;
  }
}

/* ── Response: unwrap envelope + handle 401 ───────────────────────────── */
api.interceptors.response.use(
  (response) => {
    const payload = response.data as ApiEnvelope<unknown> | undefined;
    // Every real backend response follows the { success, data } envelope (see
    // types.ts). A response that doesn't — e.g. an HTML page served by a static
    // host's SPA-fallback for a route that doesn't actually exist as an API —
    // must not be treated as valid data, or callers expecting arrays/objects
    // crash downstream (e.g. `.filter is not a function` on an HTML string).
    if (!payload || typeof payload !== 'object' || !('success' in payload)) {
      throw new ApiError('Unexpected response from server', 'INVALID_RESPONSE', response.status);
    }
    if (payload.success === false) {
      throw new ApiError(
        payload.error.message,
        payload.error.code,
        response.status,
        payload.error.details,
      );
    }
    // Mutate to bare data so callers get `T` rather than `{ success, data }`.
    response.data = payload.data;
    return response;
  },
  async (error: AxiosError<ApiEnvelope<unknown>>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const status = error.response?.status;

    if (status === 401 && !original._retried && original.url !== '/marketplace/auth/refresh') {
      original._retried = true;
      refreshInFlight ??= refreshAccessToken().finally(() => {
        refreshInFlight = null;
      });
      const newToken = await refreshInFlight;
      if (newToken) {
        original.headers = original.headers ?? {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      tokenStore.clear();
    }

    const payload = error.response?.data;
    if (payload && typeof payload === 'object' && 'success' in payload && payload.success === false) {
      throw new ApiError(payload.error.message, payload.error.code, status, payload.error.details);
    }

    throw new ApiError(
      error.message || 'Network error',
      'NETWORK_ERROR',
      status,
    );
  },
);
